import { MONEI_PREAUTH, MONEI_CONFIRM, MONEI_SAVE_CARD, MONEI_PAY_ORDER } from '../apis/payment'
import { baseURL } from '../utils/http'

/**
 * MONEI 支付通道 mixin
 * 采用 HPP（Hosted Payment Page）模式，前端不引入任何 SDK
 * 参考 Flutter monei_pay.dart 实现
 *
 * 回跳链路（WAP 无 WebView 拦截，与 Flutter 的差异）：
 * HPP 完成后 MONEI 302 到后端 redirectToResult 中转端点（后端自动追加 outTradeNo），
 * 后端再 302 到前端 /#/result 或 /#/confirm 页完成确认轮询。
 */
const MONEI_REDIRECT_ENDPOINT = `${
  baseURL.indexOf('http') === 0 ? baseURL : window.location.origin + baseURL
}cdb/payment/monei/redirectToResult`
export default {
  methods: {
    /**
     * 创建 MONEI 预授权支付（租借场景 YJ）
     * @param {Object} params
     * @param {string} params.qrcode - 机柜二维码
     * @param {number} params.paymentConfigId - MONEI 支付配置 ID
     * @param {number} [params.userCardId] - 已保存卡 ID（传入时后端用 paymentToken 直接授权）
     * @param {string} [params.couponNo] - 优惠券编号
     * @param {number} [params.rentalProductType] - 租借产品类型
     */
    moneiCreatePay (params) {
      this.$loading(true)
      const { qrcode, ...body } = params
      // 传 redirectUrl（后端自动追加 outTradeNo），HPP 完成后由 MONEI 302 回中转端点再跳前端 result 页
      body.redirectUrl = MONEI_REDIRECT_ENDPOINT
      this.$post(
        `${MONEI_PREAUTH}/${qrcode}`,
        body,
        resp => {
          this.$loading(false)
          const data = resp.data
          const paymentId = data.paymentId
          const outTradeNo = data.out_trade_no

          // 有 redirectUrl 说明必须走 HPP（PENDING 需 3DS，即使响应带了 paymentToken 也不能跳过）；
          // 无 redirectUrl 说明支付流已结束（AUTHORIZED 直接授权成功），直接轮询确认
          if (paymentId && !data.redirectUrl) {
            this._moneiConfirmAndHandle(outTradeNo, 'pay')
            return
          }

          // 打开 MONEI HPP
          const hppUrl = data.redirectUrl || `https://secure.monei.com/payments/${paymentId}`
          this._moneiRedirect(hppUrl, outTradeNo, this.payType, 'pay')
        },
        error => {
          this.$loading(false)
          this.$confirm({
            title: this.i18nMsg ? this.i18nMsg.title : 'Error',
            content: error,
            confirmText: this.i18nMsg ? this.i18nMsg.confirm : 'OK',
            showCancel: false
          })
        }
      )
    },

    /**
     * MONEI 绑卡（VERIF 0 金额验证，不扣款）
     * 回跳单参数模式：completeUrl 只由后端追加 outTradeNo，业务现场参数
     * （price/payType/cabinetID/yaJinType）作为普通表单字段传后端落库（P_REMARK），
     * 绑卡完成后 redirectToResult 按 outTradeNo 查库重建 confirm 页 URL
     * @param {string|number} paymentConfigId - MONEI 支付配置 ID
     */
    moneiSaveCard (paymentConfigId) {
      this.$loading(true)
      const q = this.$route.query || {}
      this.$post(
        `${MONEI_SAVE_CARD}?paymentConfigId=${paymentConfigId}`,
        {
          redirectUrl: MONEI_REDIRECT_ENDPOINT,
          price: q.price || '',
          payType: q.payType || '',
          cabinetID: q.cabinetID || '',
          yaJinType: q.yaJinType || ''
        },
        resp => {
          this.$loading(false)
          const data = resp.data
          const paymentId = data.paymentId
          const outTradeNo = data.outTradeNo

          // 打开 MONEI HPP（用户输入卡信息、过 3DS，0 金额不扣款）
          const hppUrl = data.redirectUrl || `https://secure.monei.com/payments/${paymentId}`
          this._moneiRedirect(hppUrl, outTradeNo, '', 'saveCard')
        },
        error => {
          this.$loading(false)
          this.$confirm({
            title: this.i18nMsg ? this.i18nMsg.title : 'Error',
            content: error,
            confirmText: this.i18nMsg ? this.i18nMsg.confirm : 'OK',
            showCancel: false
          })
        }
      )
    },

    /**
     * MONEI 订单支付（FK/PO 场景，SALE 扣款）
     * @param {Object} params
     * @param {string} params.outTradeNo - 商户订单号
     * @param {number} params.paymentConfigId - MONEI 支付配置 ID
     * @param {number} [params.userCardId] - 已保存卡 ID
     */
    moneiPayOrder (params) {
      this.$loading(true)
      const { outTradeNo, paymentConfigId, userCardId } = params
      // 传 redirectUrl（后端自动追加 outTradeNo），HPP 完成后由 MONEI 302 回中转端点再跳前端 result 页
      let url = `${MONEI_PAY_ORDER}?outTradeNo=${outTradeNo}&paymentConfigId=${paymentConfigId}`
      if (userCardId) {
        url += `&userCardId=${userCardId}`
      }
      url += `&redirectUrl=${encodeURIComponent(MONEI_REDIRECT_ENDPOINT)}`
      this.$post(
        url,
        {},
        resp => {
          this.$loading(false)
          const data = resp.data
          const newOutTradeNo = data.outTradeNo || outTradeNo
          const paymentStatus = data.paymentStatus || ''

          // 直接成功
          if (paymentStatus === 'SUCCEEDED' || paymentStatus === 'AUTHORIZED') {
            this._moneiPaySuccess(newOutTradeNo)
            return
          }

          // 需要 HPP 确认（3DS）
          if (data.redirectUrl) {
            this._moneiRedirect(data.redirectUrl, newOutTradeNo, this.payType, 'pay')
            return
          }

          // 异常：既没有成功也没有 redirectUrl
          this.$confirm({
            title: this.i18nMsg ? this.i18nMsg.title : 'Error',
            content: this.i18nMsg ? this.i18nMsg.error : 'Payment failed',
            confirmText: this.i18nMsg ? this.i18nMsg.confirm : 'OK',
            showCancel: false
          })
        },
        error => {
          this.$loading(false)
          this.$confirm({
            title: this.i18nMsg ? this.i18nMsg.title : 'Error',
            content: error,
            confirmText: this.i18nMsg ? this.i18nMsg.confirm : 'OK',
            showCancel: false
          })
        }
      )
    },

    /**
     * 轮询确认 MONEI 支付状态
     * @param {string} outTradeNo - 商户订单号
     * @param {number} index - 当前重试次数（从 0 开始）
     * @returns {Promise<Object>} {code: 0|−1, outTradeNo, msg?}
     */
    _moneiConfirmPayment (outTradeNo, index) {
      return new Promise((resolve, reject) => {
        this.$post(
          MONEI_CONFIRM,
          { outTradeNo: outTradeNo },
          resp => {
            const data = resp.data
            if (
              data.success === 'true' ||
              data.success === true ||
              data.status === '免押租借成功' ||
              data.status === '预授权成功' ||
              data.status === 'AUTHORIZED' ||
              data.status === '支付成功' ||
              data.moneiStatus === 'AUTHORIZED'
            ) {
              resolve({ code: 0, outTradeNo: outTradeNo })
            } else if (index < 4) {
              setTimeout(() => {
                this._moneiConfirmPayment(outTradeNo, index + 1).then(resolve).catch(reject)
              }, 3000)
            } else {
              resolve({ code: -1, outTradeNo: outTradeNo, msg: 'Payment confirmation timeout' })
            }
          },
          error => {
            if (index < 4) {
              setTimeout(() => {
                this._moneiConfirmPayment(outTradeNo, index + 1).then(resolve).catch(reject)
              }, 3000)
            } else {
              reject(error)
            }
          }
        )
      })
    },

    /**
     * HPP 跳转前保存状态并重定向
     */
    _moneiRedirect (url, outTradeNo, payType, action) {
      sessionStorage.setItem('monei_outTradeNo', outTradeNo)
      sessionStorage.setItem('monei_payType', payType || '')
      sessionStorage.setItem('monei_action', action)
      // 保存当前路由参数，以便 HPP 返回后恢复页面状态
      sessionStorage.setItem('monei_returnQuery', JSON.stringify(this.$route.query))
      window.location.href = url
    },

    /**
     * 确认支付后根据 action 处理结果
     */
    _moneiConfirmAndHandle (outTradeNo, action) {
      this.$loading(true)
      this._moneiConfirmPayment(outTradeNo, 0).then(result => {
        this.$loading(false)
        if (result.code === 0) {
          this._moneiPaySuccess(outTradeNo)
        } else {
          this.$confirm({
            title: this.i18nMsg ? this.i18nMsg.title : 'Error',
            content: result.msg || (this.i18nMsg ? this.i18nMsg.error : 'Payment failed'),
            confirmText: this.i18nMsg ? this.i18nMsg.confirm : 'OK',
            showCancel: false
          })
        }
      }).catch(error => {
        this.$loading(false)
        this.$confirm({
          title: this.i18nMsg ? this.i18nMsg.title : 'Error',
          content: error || (this.i18nMsg ? this.i18nMsg.error : 'Payment failed'),
          confirmText: this.i18nMsg ? this.i18nMsg.confirm : 'OK',
          showCancel: false
        })
      })
    },

    /**
     * MONEI 支付成功后跳转
     */
    _moneiPaySuccess (outTradeNo) {
      if (this.payType === 'FK') {
        this.$router.replace(`/result?tradeNo=${outTradeNo}&payMode=monei&payType=FK&confirmed=1`)
      } else {
        this.$router.replace(`/result?tradeNo=${outTradeNo}&payMode=monei&confirmed=1`)
      }
    }
  }
}
